import React from 'react';
import { FaArrowLeft, FaArrowRight, FaFolderOpen } from 'react-icons/fa';
import useInView from '../Animation/useInView.ts';
import { useResponsiveCarousel } from '../../hooks/useResponsiveCarousel.ts';
import styles from './ProjectsShowcase.module.css';

interface ProjectCard {
    id: string;
    title: string;
    description: string;
    projectUrl?: string | null;
    imagePath?: string | null;
    technologies: string[];
}

interface ProjectsShowcaseProps {
    title: string;
    subtitle?: string;
    projects: ProjectCard[];
}

const ProjectsShowcase: React.FC<ProjectsShowcaseProps> = ({ title, subtitle, projects }) => {
    const { ref, isInView } = useInView({ threshold: 0.12, triggerOnce: true });

    const {
        canSlide,
        direction,
        isTransitioning,
        next,
        prev,
        visibleItems,
    } = useResponsiveCarousel(projects);

    const carouselClassName = [
        styles.carousel,
        isTransitioning ? styles[direction] : '',
    ].filter(Boolean).join(' ');

    const getSlot = (position: number) => {
        if (visibleItems.length === 1) return 0;
        return position - Math.min(1, visibleItems.length - 1);
    };

    const renderProject = (project: ProjectCard, slot: number) => {
        const hasUrl = Boolean(project.projectUrl);
        const isActive = slot === 0;
        const style = {
            '--slot': slot,
            '--delay': `${0.12 + Math.abs(slot) * 0.05}s`
        } as React.CSSProperties;

        const content = (
            <>
                <div className={styles.cardTop}>
                    <div className={styles.titleBlock}>
                        <h3>{project.title}</h3>
                    </div>
                </div>

                <div className={styles.media}>
                    {project.imagePath ? (
                        <img src={project.imagePath} alt={project.title} loading="lazy" />
                    ) : (
                        <div className={styles.placeholder}>
                            <FaFolderOpen />
                        </div>
                    )}
                </div>

                <div className={styles.body}>
                    <p className={styles.description}>{project.description}</p>

                    {!!project.technologies.length && (
                        <div className={styles.techs}>
                            {project.technologies.slice(0, 4).map(technology => (
                                <span key={technology}>{technology}</span>
                            ))}
                        </div>
                    )}
                </div>
            </>
        );

        const className = [
            styles.card,
            isActive ? styles.activeCard : styles.sideCard,
            !hasUrl ? styles.staticCard : '',
        ].filter(Boolean).join(' ');

        if (!hasUrl) {
            return (
                <article key={`${project.id}-${slot}`} className={className} style={style}>
                    {content}
                </article>
            );
        }

        return (
            <a
                key={`${project.id}-${slot}`}
                className={className}
                href={project.projectUrl || undefined}
                target="_blank"
                rel="noreferrer"
                style={style}
                aria-label={`Открыть проект ${project.title}`}
            >
                {content}
            </a>
        );
    };

    return (
        <section id="Projects" ref={ref} className={`${styles.section} ${isInView ? styles.visible : ''}`}>
            <div className={styles.container}>
                <div className={styles.header}>
                    <h2 className={styles.title}>{title}</h2>
                    {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
                </div>

                <div className={carouselClassName} aria-live="polite">
                    {visibleItems.map(({ item: project, position }) => renderProject(project, getSlot(position)))}
                </div>

                {canSlide && (
                    <div className={styles.controls}>
                        <button type="button" className={styles.arrowButton} onClick={prev} disabled={isTransitioning} aria-label="Предыдущий проект">
                            <FaArrowLeft />
                        </button>
                        <button type="button" className={styles.arrowButton} onClick={next} disabled={isTransitioning} aria-label="Следующий проект">
                            <FaArrowRight />
                        </button>
                    </div>
                )}
            </div>
        </section>
    );
};

export default ProjectsShowcase;
